/**
 * The playEngine editors' shared view of localization.
 *
 * A snapshot's `localization` block holds three things: which language is on
 * screen, which languages exist, and the per-language overrides. Overrides come
 * in two shapes and neither is stored on the thing it overrides —
 *
 *   overrides[lang][componentId]        component config + dependencies
 *   globalVariableOverrides[lang][path] one leaf of the globalVariables tree
 *
 * — so "English" is always the value sitting in `components` / `globalVariables`
 * and every other language is that value with a sparse patch on top.
 *
 * Version Settings and Custom Variables are separate panels editing the same
 * block, so the language switch lives here rather than in either of them.
 */

import _ from "lodash";
import { setIn } from "./setIn";
import { sendCommand } from "./playEngineBridge";

/** The base language: its values live in the snapshot itself, not in an override. */
export const BASE_LANGUAGE = "en";

/** True when edits should be written as overrides rather than onto the base. */
export function isOverrideLanguage(language) {
  return Boolean(language) && language !== BASE_LANGUAGE;
}

/**
 * The dotted-path → value map for one language, or {} when there is none.
 * Never returns the stored object itself — callers build the next map from it.
 */
export function getGlobalVariableOverrides(playEngineConfig, language) {
  if (!isOverrideLanguage(language)) return {};
  const stored = _.get(playEngineConfig, ["localization", "globalVariableOverrides", language]);
  return _.isPlainObject(stored) ? _.cloneDeep(stored) : {};
}

/**
 * Base variables with one language's overrides laid over them.
 *
 * The base tree is what gets persisted as `globalVariables` and must never pick
 * up a translation by reference. `setIn` guarantees that without copying the
 * tree: it rebuilds only the nodes on each overridden path and shares the rest.
 *
 * WHY THAT MATTERS HERE AND NOT ONLY FOR SPEED. The panels read field values out
 * of this result, and a control keeps local editing state that follows its
 * value. Deep-cloning handed every field a brand new object on every call — so
 * every notification from the variable store looked like "your value changed" to
 * a responsive pair or a vec2, and the edit in progress was thrown away. In the
 * common case of no overrides at all, the base is now returned unchanged, which
 * is both the cheapest and the most stable answer.
 */
export function applyGlobalVariableOverrides(baseVariables, overrides) {
  const base = baseVariables || {};
  if (!_.isPlainObject(overrides)) return base;

  // A compound control may write `layout.position` while Custom Variables edits
  // `layout.position.x`. Object insertion order must not decide which one wins:
  // apply containers first and their more-specific leaves afterwards.
  const entries = Object.entries(overrides)
    .filter(([, value]) => value !== undefined)
    .sort(([left], [right]) => left.split(".").length - right.split(".").length);

  let merged = base;
  for (const [path, value] of entries) {
    merged = setIn(merged, path.split("."), _.cloneDeep(value));
  }
  return merged;
}

/**
 * Switch the editors — engine and stored config alike — to `language`.
 *
 * Registers the language with the engine the first time it is used, tells the
 * engine to switch, then writes the whole `localization` block back. The whole
 * block rather than `currentLanguage` alone because a first-time language also
 * grows `availableLanguages` and its two (empty) override buckets, and one
 * write keeps them from arriving separately.
 *
 * Preview failures are warnings, not errors: the language must still be
 * persisted, or the panels and the snapshot would disagree about what is on
 * screen. The caller updates its own state and re-reads afterwards.
 *
 * @param {object}   args
 * @param {string}   args.iFrameId
 * @param {object}   args.selectedVersion
 * @param {function} args.setSelectedVersionPathValue
 * @param {string}   args.language
 */
export async function switchPlayEngineLanguage({
  iFrameId,
  selectedVersion,
  setSelectedVersionPathValue,
  language,
}) {
  const config = _.cloneDeep(_.get(selectedVersion, "playEngineConfig") || {});
  if (!config.localization) {
    config.localization = {
      currentLanguage: BASE_LANGUAGE,
      availableLanguages: [BASE_LANGUAGE],
      overrides: null,
    };
  }

  const available = config.localization.availableLanguages || [BASE_LANGUAGE];
  if (!available.includes(language)) {
    try {
      await sendCommand(iFrameId, {
        type: "playengine:dispatch-action",
        action: {
          target: "engine",
          name: "addLanguage",
          args: { languageCode: language },
        },
      });
    } catch (err) {
      console.warn("[switchPlayEngineLanguage] addLanguage failed:", err.message);
    }
    available.push(language);
    config.localization.availableLanguages = available;

    if (!config.localization.overrides) config.localization.overrides = {};
    if (!config.localization.overrides[language]) config.localization.overrides[language] = {};
    if (!config.localization.globalVariableOverrides) config.localization.globalVariableOverrides = {};
    if (!config.localization.globalVariableOverrides[language]) {
      config.localization.globalVariableOverrides[language] = {};
    }
  }

  try {
    await sendCommand(iFrameId, {
      type: "playengine:dispatch-action",
      action: {
        target: "engine",
        name: "switchLanguage",
        args: { languageCode: language },
      },
    });
  } catch (err) {
    console.warn("[switchPlayEngineLanguage] switchLanguage failed:", err.message);
  }

  config.localization.currentLanguage = language;
  setSelectedVersionPathValue({ path: "playEngineConfig", value: config });
}
